import { BestHealthLogo } from "layout/left-menu/BestHealthLogo";
import { LeftMenuDrawer } from "layout/left-menu/LeftMenuDrawer";
import { DrawerOpenState } from "layout/left-menu/types";
import { useCallback, useEffect, useRef, useState } from "react";

import { Box, styled, useTheme } from "@mui/material";
import { DrawerProps } from "@mui/material/Drawer";

import { MenuItems } from "./MenuItems";

interface Props extends Omit<DrawerProps, "open" | "onClose"> {
  openState: DrawerOpenState;
  onOpenToggle?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const LogoBar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(1, 1, 0.9, 2)
}));

const useMouseEnterLeave = (): [boolean, () => void, () => void] => {
  const [hasEntered, setHasEntered] = useState(false);
  const handleEnter = useCallback(() => setHasEntered(true), []);
  const handleLeave = useCallback(() => setHasEntered(false), []);

  return [hasEntered, handleEnter, handleLeave];
};

const LeftMenu = (props: Props) => {
  const {
    openState,
    onOpenToggle,
    onMouseEnter,
    onMouseLeave,
    ...drawerProps
  } = props;

  const isOpen = openState >= DrawerOpenState.Open;
  const theme = useTheme();
  const isHoveringRef = useRef<boolean>(false);
  const [isHoveringBox, handleMouseEnterBox, handleMouseLeaveBox] =
    useMouseEnterLeave();

  const [isHoveringButton] = useMouseEnterLeave();

  useEffect(() => {
    const isHovering = isHoveringRef.current;

    if (!isHovering && isHoveringBox && !isHoveringButton) {
      // Only open the box if the user is hovering over it, but not over the expand button
      isHoveringRef.current = true;
      onMouseEnter();
    } else if (isHovering && !(isHoveringBox || isHoveringButton)) {
      // Keep the menu open as long as the user is hovering over the box or the button
      isHoveringRef.current = false;
      onMouseLeave();
    }
  }, [isHoveringBox, isHoveringButton, onMouseEnter, onMouseLeave]);

  return (
    <LeftMenuDrawer
      openState={openState}
      onClose={onOpenToggle}
      {...drawerProps}
    >
      <Box
        onMouseEnter={handleMouseEnterBox}
        onMouseLeave={handleMouseLeaveBox}
        sx={{
          display: "flex",
          flexFlow: "column",
          height: "100%",
          background: theme.palette.header.main
        }}
      >
        <LogoBar>
          <BestHealthLogo open={isOpen} />
        </LogoBar>
        <Box flex="1 1 auto">
          <MenuItems />
        </Box>
      </Box>
    </LeftMenuDrawer>
  );
};

// eslint-disable-next-line import/no-default-export
export default LeftMenu;
